// Modals: API info, Agent manager, Data manager
import React, { useEffect, useState, useCallback } from 'react';
import { X, Plus, Trash2, Download, Upload, Database, Key, Bot, FileCheck2 } from 'lucide-react';
import { db, uid } from '../lib/db';
import { useToast } from './Toast';

export const ApiModal = ({ open, onClose }) => {
  const toast = useToast();
  const [mode, setMode] = useState('emergent'); // 'emergent' | 'universal' | 'custom'
  const [universalKey, setUniversalKey] = useState('');
  const [openaiKey, setOpenaiKey] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [geminiKey, setGeminiKey] = useState('');
  const [showKeys, setShowKeys] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const m = await db.get('settings', 'api_mode');
      const uk = await db.get('settings', 'api_key_universal');
      const ok = await db.get('settings', 'api_key_openai');
      const ak = await db.get('settings', 'api_key_anthropic');
      const gk = await db.get('settings', 'api_key_gemini');
      setMode(m?.value || 'emergent');
      setUniversalKey(uk?.value || '');
      setOpenaiKey(ok?.value || '');
      setAnthropicKey(ak?.value || '');
      setGeminiKey(gk?.value || '');
    })();
  }, [open]);

  if (!open) return null;

  const save = async () => {
    if (mode === 'universal' && !universalKey.trim()) {
      toast('Veuillez saisir votre clé universelle (sk-emergent-...)', 'error');
      return;
    }
    await db.put('settings', { id: 'api_mode', value: mode });
    await db.put('settings', { id: 'api_key_universal', value: universalKey.trim() });
    await db.put('settings', { id: 'api_key_openai', value: openaiKey.trim() });
    await db.put('settings', { id: 'api_key_anthropic', value: anthropicKey.trim() });
    await db.put('settings', { id: 'api_key_gemini', value: geminiKey.trim() });
    const label =
      mode === 'emergent' ? 'Clé Emergent par défaut activée' :
      mode === 'universal' ? 'Clé universelle personnelle activée — tous les modèles débloqués !' :
      'Clés providers personnelles activées';
    toast(label, 'success');
    onClose();
  };

  const maskInput = showKeys ? 'text' : 'password';

  return (
    <div className='modal-overlay active' onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className='modal' style={{ maxWidth: 640 }}>
        <div className='modal-header'>
          <span className='modal-title'>API CONFIG</span>
          <button className='modal-close' onClick={onClose}><X size={14} /></button>
        </div>
        <div className='modal-body'>
          <div className='modal-section'>
            <label className='modal-label'>▸ Source des clés API</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
              <button
                data-testid='api-mode-emergent'
                className={`hud-btn ${mode === 'emergent' ? 'primary' : ''}`}
                style={{ justifyContent: 'center' }}
                onClick={() => setMode('emergent')}
              >
                <Key size={11} /> DÉFAUT
              </button>
              <button
                data-testid='api-mode-universal'
                className={`hud-btn ${mode === 'universal' ? 'primary' : ''}`}
                style={{ justifyContent: 'center' }}
                onClick={() => setMode('universal')}
              >
                <Key size={11} /> MA CLÉ UNIV.
              </button>
              <button
                data-testid='api-mode-custom'
                className={`hud-btn ${mode === 'custom' ? 'primary' : ''}`}
                style={{ justifyContent: 'center' }}
                onClick={() => setMode('custom')}
              >
                <Key size={11} /> PROVIDERS
              </button>
            </div>

            {mode === 'emergent' && (
              <div style={{ background: 'var(--hull)', border: 'var(--hud-border)', borderRadius: 6, padding: 12 }}>
                <p style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6, margin: 0 }}>
                  YNX utilise la <strong style={{ color: 'var(--cyan)' }}>clé Emergent du serveur</strong> (par défaut).
                  Accès immédiat aux <strong>16+ modèles</strong> (GPT-5, Claude Sonnet/Opus 4, Gemini 2.5/3).
                </p>
              </div>
            )}

            {mode === 'universal' && (
              <>
                <p style={{ fontSize: 12, color: 'var(--neon)', lineHeight: 1.6, marginBottom: 10 }}>
                  ★ <strong>Mode gratuit</strong> : collez votre <strong>clé Emergent universelle personnelle</strong> (sk-emergent-...) pour utiliser votre propre balance et débloquer tous les providers d'un coup.
                </p>
                <label className='modal-label'>▸ Clé Emergent universelle</label>
                <input
                  data-testid='input-universal-key'
                  name='universal-key'
                  className='hud-input'
                  type={maskInput}
                  placeholder='sk-emergent-xxxxxxxxxxxxxxxx'
                  value={universalKey}
                  onChange={(e) => setUniversalKey(e.target.value)}
                  style={{ marginBottom: 10 }}
                />
                <p style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.5 }}>
                  ▸ Obtenir une clé : <a href='https://app.emergent.sh' target='_blank' rel='noreferrer' style={{ color: 'var(--cyan)' }}>emergent.sh</a> → Profile → Universal Key.<br />
                  ▸ Votre clé est stockée localement (IndexedDB) et envoyée uniquement pour relayer les requêtes.
                </p>
              </>
            )}

            {mode === 'custom' && (
              <>
                <p style={{ fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.6, marginBottom: 12 }}>
                  Saisissez vos clés API natives. Stockage local, transmission uniquement au provider sélectionné par le modèle.
                </p>

                <label className='modal-label'>▸ OpenAI API Key (GPT-5, GPT-4o, o3)</label>
                <input
                  data-testid='input-openai-key'
                  name='openai-key'
                  className='hud-input'
                  type={maskInput}
                  placeholder='sk-...'
                  value={openaiKey}
                  onChange={(e) => setOpenaiKey(e.target.value)}
                  style={{ marginBottom: 10 }}
                />

                <label className='modal-label'>▸ Anthropic API Key (Claude Sonnet/Opus)</label>
                <input
                  data-testid='input-anthropic-key'
                  name='anthropic-key'
                  className='hud-input'
                  type={maskInput}
                  placeholder='sk-ant-...'
                  value={anthropicKey}
                  onChange={(e) => setAnthropicKey(e.target.value)}
                  style={{ marginBottom: 10 }}
                />

                <label className='modal-label'>▸ Google Gemini API Key</label>
                <input
                  data-testid='input-gemini-key'
                  name='gemini-key'
                  className='hud-input'
                  type={maskInput}
                  placeholder='AIza...'
                  value={geminiKey}
                  onChange={(e) => setGeminiKey(e.target.value)}
                  style={{ marginBottom: 10 }}
                />
              </>
            )}

            {(mode === 'universal' || mode === 'custom') && (
              <label style={{ fontSize: 11, color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginTop: 8 }}>
                <input type='checkbox' name='show-keys' checked={showKeys} onChange={(e) => setShowKeys(e.target.checked)} />
                Afficher les clés
              </label>
            )}
          </div>

          <div className='modal-section'>
            <label className='modal-label'>▸ Providers disponibles</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span className='tag'>OpenAI · 8 modèles</span>
              <span className='tag'>Anthropic · 4 modèles</span>
              <span className='tag'>Google Gemini · 4 modèles</span>
            </div>
          </div>

          <div className='modal-section'>
            <label className='modal-label'>▸ Confidentialité</label>
            <p style={{ fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.6 }}>
              Toutes vos conversations, agents et mémoires sont stockés <strong>localement</strong> dans votre navigateur (IndexedDB).
              Aucune donnée ne quitte votre appareil sauf le message envoyé au modèle.
            </p>
          </div>
        </div>
        <div className='modal-footer'>
          <button className='hud-btn' onClick={onClose}>ANNULER</button>
          <button data-testid='api-save-btn' className='hud-btn primary' onClick={save}><Key size={12} /> ENREGISTRER</button>
        </div>
      </div>
    </div>
  );
};

export const AgentModal = ({ open, onClose, currentAgent, onAgentChange }) => {
  const toast = useToast();
  const [agents, setAgents] = useState([]);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [instr, setInstr] = useState('');
  const [tags, setTags] = useState('');

  const load = useCallback(async () => {
    const list = await db.getAll('agents');
    setAgents(list);
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const save = async () => {
    if (!name.trim() || !instr.trim()) { toast('Nom et instructions requis', 'error'); return; }
    const agent = {
      id: uid(),
      name: name.trim(),
      desc: desc.trim(),
      instructions: instr.trim(),
      tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      createdAt: Date.now(),
    };
    await db.put('agents', agent);
    setName(''); setDesc(''); setInstr(''); setTags('');
    await load();
    toast(`Agent « ${agent.name} » créé`, 'success');
    onAgentChange?.(agent);
  };

  const activate = (a) => { onAgentChange?.(a); toast(`Agent « ${a.name} » activé`, 'success'); onClose(); };
  const remove = async (id) => {
    await db.delete('agents', id);
    await load();
    if (currentAgent?.id === id) onAgentChange?.(null);
    toast('Agent supprimé', 'info');
  };
  const deactivate = () => { onAgentChange?.(null); toast('Aucun agent actif', 'info'); };

  return (
    <div className='modal-overlay active' onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className='modal'>
        <div className='modal-header'>
          <span className='modal-title'>AGENT MANAGER</span>
          <button className='modal-close' onClick={onClose}><X size={14} /></button>
        </div>
        <div className='modal-body'>
          <div className='modal-section'>
            <label className='modal-label'>▸ Agents existants ({agents.length})</label>
            {agents.length === 0 && <p style={{ fontSize: 12, color: 'var(--text-dim)' }}>Aucun agent personnalisé. Créez-en un ci-dessous.</p>}
            {agents.map((a) => (
              <div key={a.id} className={`agent-item ${currentAgent?.id === a.id ? 'active' : ''}`}>
                <div className='agent-name'>{a.name}</div>
                {a.desc && <div className='agent-desc'>{a.desc}</div>}
                {a.tags?.length > 0 && (
                  <div className='agent-tags'>{a.tags.map((t) => <span key={t} className='tag'>{t}</span>)}</div>
                )}
                <div className='agent-actions'>
                  <button className='hud-btn primary' onClick={() => activate(a)}><Bot size={11} /> ACTIVER</button>
                  <button className='hud-btn danger icon-only' onClick={() => remove(a.id)}><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
            {currentAgent && <button className='hud-btn' style={{ marginTop: 10 }} onClick={deactivate}>DÉSACTIVER L'AGENT ACTUEL</button>}
          </div>

          <div className='modal-section' style={{ borderTop: '1px dashed var(--wire)', paddingTop: 16 }}>
            <label className='modal-label'>▸ Nouvel agent</label>
            <input data-testid='agent-name' name='agent-name' className='hud-input' placeholder='Nom (ex: Code Wizard)' value={name} onChange={(e) => setName(e.target.value)} style={{ marginBottom: 8 }} />
            <input data-testid='agent-desc' name='agent-desc' className='hud-input' placeholder='Description courte' value={desc} onChange={(e) => setDesc(e.target.value)} style={{ marginBottom: 8 }} />
            <textarea data-testid='agent-instructions' name='agent-instructions' className='hud-textarea' placeholder='Instructions système (le persona, le style, les règles…)' value={instr} onChange={(e) => setInstr(e.target.value)} style={{ marginBottom: 8 }} />
            <input data-testid='agent-tags' name='agent-tags' className='hud-input' placeholder='Tags (séparés par des virgules)' value={tags} onChange={(e) => setTags(e.target.value)} />
          </div>
        </div>
        <div className='modal-footer'>
          <button className='hud-btn' onClick={onClose}>FERMER</button>
          <button data-testid='agent-create-btn' className='hud-btn primary' onClick={save}><Plus size={12} /> CRÉER L'AGENT</button>
        </div>
      </div>
    </div>
  );
};

export const DataModal = ({ open, onClose, onAfterImport }) => {
  const toast = useToast();
  const [stats, setStats] = useState({ chats: 0, agents: 0, mems: 0 });
  const [pendingFile, setPendingFile] = useState(null);
  const [previewInfo, setPreviewInfo] = useState(null);
  const [drag, setDrag] = useState(false);

  const computeStats = useCallback(async () => {
    const [chats, agents, mems] = await Promise.all([
      db.getAll('chats'),
      db.getAll('agents'),
      db.getAll('global_memory'),
    ]);
    setStats({ chats: chats.length, agents: agents.length, mems: mems.length });
  }, []);

  useEffect(() => { if (open) computeStats(); }, [open, computeStats]);

  if (!open) return null;

  const exportData = async () => {
    const [chats, agents, settings, global_memory] = await Promise.all([
      db.getAll('chats'), db.getAll('agents'), db.getAll('settings'), db.getAll('global_memory'),
    ]);
    const payload = {
      app: 'YNX',
      version: 1,
      exported: new Date().toISOString(),
      data: { chats, agents, settings, global_memory },
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ynx-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast('Sauvegarde exportée', 'success');
  };

  const handleFile = async (file) => {
    setPendingFile(file);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const data = payload.data || payload;
      setPreviewInfo({
        name: file.name,
        size: (file.size / 1024).toFixed(1),
        chats: data.chats?.length || 0,
        agents: data.agents?.length || 0,
        mems: data.global_memory?.length || 0,
        exported: payload.exported,
        valid: true,
      });
    } catch (e) {
      setPreviewInfo({ name: file.name, valid: false, error: e.message });
    }
  };

  const confirmImport = async () => {
    if (!pendingFile || !previewInfo?.valid) return;
    const text = await pendingFile.text();
    const payload = JSON.parse(text);
    const data = payload.data || payload;
    const map = { chats: 'chats', agents: 'agents', settings: 'settings', global_memory: 'global_memory' };
    for (const [k, store] of Object.entries(map)) {
      if (Array.isArray(data[k])) {
        for (const item of data[k]) await db.put(store, item);
      }
    }
    setPendingFile(null); setPreviewInfo(null);
    toast('Données restaurées avec succès', 'success');
    await computeStats();
    onAfterImport?.();
  };

  const clearAll = async () => {
    if (!window.confirm('⚠ Supprimer TOUTES les données YNX ? Cette action est irréversible.')) return;
    for (const s of ['chats', 'agents', 'settings', 'global_memory']) await db.clear(s);
    await computeStats();
    toast('Toutes les données supprimées', 'success');
    onAfterImport?.();
  };

  return (
    <div className='modal-overlay active' onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className='modal'>
        <div className='modal-header'>
          <span className='modal-title'>DATA CENTER</span>
          <button className='modal-close' onClick={onClose}><X size={14} /></button>
        </div>
        <div className='modal-body'>
          <div className='stat-grid'>
            <div className='stat-card'><div className='stat-value'>{stats.chats}</div><div className='stat-label'>Conversations</div></div>
            <div className='stat-card'><div className='stat-value'>{stats.agents}</div><div className='stat-label'>Agents</div></div>
            <div className='stat-card'><div className='stat-value'>{stats.mems}</div><div className='stat-label'>Mémoires</div></div>
          </div>

          <div className='modal-section'>
            <label className='modal-label'>▸ Importer une sauvegarde</label>
            <div
              className={`drop-zone ${drag ? 'dragover' : ''}`}
              onClick={() => document.getElementById('voanh-import-file').click()}
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
            >
              <Upload size={18} style={{ marginBottom: 6 }} />
              <div>Déposez votre fichier .json ou cliquez ici</div>
              <input id='voanh-import-file' type='file' accept='application/json' style={{ display: 'none' }} onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])} />
            </div>
            {previewInfo && (
              <div style={{ background: 'var(--hull)', border: 'var(--hud-border)', borderRadius: 6, padding: 12, marginTop: 10, fontSize: 12 }}>
                <div style={{ color: 'var(--text-bright)', marginBottom: 4 }}><strong>{previewInfo.name}</strong></div>
                {previewInfo.valid ? (
                  <>
                    <div style={{ color: 'var(--neon)' }}><FileCheck2 size={12} style={{ marginRight: 6, verticalAlign: 'middle' }} />Fichier valide ({previewInfo.size} KB)</div>
                    <div style={{ color: 'var(--text-dim)', marginTop: 4 }}>{previewInfo.chats} conv. · {previewInfo.agents} agents · {previewInfo.mems} souvenirs</div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button className='hud-btn primary' onClick={confirmImport}>VALIDER & RESTAURER</button>
                      <button className='hud-btn' onClick={() => { setPendingFile(null); setPreviewInfo(null); }}>ANNULER</button>
                    </div>
                  </>
                ) : (
                  <div style={{ color: 'var(--danger)' }}>⚠ Fichier invalide : {previewInfo.error}</div>
                )}
              </div>
            )}
          </div>

          <div className='modal-section'>
            <label className='modal-label'>▸ Exporter / Réinitialiser</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className='hud-btn primary' onClick={exportData}><Download size={12} /> EXPORTER TOUT</button>
              <button className='hud-btn danger' onClick={clearAll}><Trash2 size={12} /> EFFACER TOUT</button>
            </div>
          </div>
        </div>
        <div className='modal-footer'>
          <button className='hud-btn' onClick={onClose}>FERMER</button>
        </div>
      </div>
    </div>
  );
};
